import { Loader2, X } from "lucide-react";

import { useAppSelector } from "../../../../src/app/store";
import { cn } from "../../../../src/lib/utils";

import {
  selectDocumentDeleteError,
  selectIsDocumentDeleting,
} from "../logic/caseDocumentsSelector";

import InlineError from "./InlineError";
import { deleteCaseDocument } from "../../../../src/thunks/caseDocument";
import { useAppDispatch } from "../../../../src/app/hooks";
import { showSuccessToast } from "../../../../src/lib/toast";
import { useParams } from "react-router-dom";

interface DeleteDocumentModalProps {
  onClose: () => void;
  fileName: string;
  selectedDocumentId: string;
}

export default function DeleteDocumentModal({
  onClose,
  fileName,
  selectedDocumentId,
}: DeleteDocumentModalProps) {
  const isDeleting = useAppSelector(
    selectIsDocumentDeleting(selectedDocumentId)
  );
  const deleteError = useAppSelector(
    selectDocumentDeleteError(selectedDocumentId)
  );

  const { id: caseId } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();  

  const handleConfirmDelete = async () => {
  if (!selectedDocumentId || !caseId) return;

  try {
    await dispatch(
      deleteCaseDocument({
        case_id: caseId,
        document_id: selectedDocumentId,
      })
    ).unwrap();

    showSuccessToast("Document has been deleted successfully");
    onClose();
  } catch (error) {
    console.error("Failed to delete document:", error);
  }
};

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[rgba(10,10,10,0.20)]">
      
      <div
        className="
          relative flex flex-col items-end gap-4
          w-[425px] max-w-[425px]
          p-6
          rounded-lg
          border border-[#E5E5E5]
          bg-white
          shadow-lg
        "
      >

        <button
          onClick={onClose}
          disabled={isDeleting}
          className={cn(
            "absolute right-4 top-4 text-gray-700 rounded-xs",
            "hover:text-gray-900 hover:bg-gray-100",
            "disabled:text-gray-300 disabled:hover:text-gray-300",
            "disabled:hover:bg-transparent disabled:cursor-not-allowed"
          )}
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex flex-col items-center gap-[6px] self-stretch">
          <div className="w-full text-center">
            <p
              className="
                mx-auto max-w-[360px]
                truncate
                text-lg font-semibold leading-4 text-[#0A0A0A]
                font-sans
              "
              title={fileName}
            >
              Delete “{fileName}”
            </p>
          </div>

          <div className="w-full text-center">
            <p
              className="
                text-sm leading-5 text-[#737373]
                font-sans
              "
            >
              Do you want to delete the document?
            </p>
          </div>
        </div>

        <div className="flex w-full items-center justify-center gap-2">

          <button
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            className="
              flex h-9 flex-1 items-center justify-center gap-2
              rounded-md
              bg-[#DC2626] px-4 py-2
              text-white
              shadow-xs
              font-sans
              disabled:opacity-50
            "
          >
            {isDeleting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin text-[#FAFAFA]" />
                <span className="text-sm font-medium leading-5 text-[#FAFAFA]">
                  Deleting...
                </span>
              </>
            ) : (
              <span className="text-sm font-medium leading-5 text-[#FAFAFA]">
                Delete
              </span>
            )}
          </button>

          <button
            onClick={onClose}
            disabled={isDeleting}
            className="
              flex h-9 flex-1 items-center justify-center
              rounded-md
              border border-[#E5E5E5]
              bg-white px-4 py-2
              text-[#0A0A0A]
              shadow-xs
              font-sans
              disabled:cursor-not-allowed
              disabled:opacity-50
            "
          >
            <span className="text-sm font-medium leading-5 text-[#0A0A0A]">
              Cancel
            </span>
          </button>
        </div>

        {deleteError && (
          <InlineError
            message={`${deleteError} delete operation failed. Please retry!`}
          />
        )}
      </div>
    </div>
  );
}
